import { DateTime } from 'luxon';
import { useEffect, useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';

import CloseIcon from '@mui/icons-material/Close';
import {
  Dialog,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from '@mui/material';

import {
  DialogContentFormContainer,
  DialogMainContent,
} from '../dialog/dialogStyledComponents';
import {
  DescriptionContainer,
  EventContainer,
  EventFieldsContainer,
  Section,
  ToggleFormGroup,
} from './sectionStyledComponents';

import {
  ControlledDateField,
  ControlledRadioGroup,
  ControlledTextField,
} from '@act/storybook';
import Loader from './loader';

import { CreateScheduledEventRequestDto } from '@act/shared/data-transfer-objects';
import { createScheduledEventForUser } from 'redux/slices/scheduledEventSlice';
import { createWorkflowScheduledEvent } from 'redux/slices/workflowSlice';
import { useAppDispatch } from 'redux/store-hooks';

import {
  ControlledCheckbox,
  ControlledSelect,
  SelectOption,
} from '@act/storybook';
import { scheduledEventRequests } from 'redux/requests/scheduled-event.requests';
import { useApiCall } from 'redux/utils/api-hook';
import { scheduledEventDateTimeHelpers } from 'utilities/date-time-generator';
import { DialogFooter } from '../dialog/dialog-footer';
import { DialogHeader } from '../dialog/dialog-header';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  EventType,
  IAttachToEventForm,
  IAttachToEventFormSchema,
} from './workflow-event-dialog-form-types';

interface AttachWorkflowDialogProps {
  open: boolean;
  handleClose: () => void;
  description: string;
  assignedWorkflowPhaseId: number;
  userId: string;
}

const defaultValues: Partial<IAttachToEventForm> = {
  useExisting: false,
  existingEventId: null,
  existingEventNote: 'No event selected',
  eventType: undefined,
  date: DateTime.now().toFormat('yyyy-MM-dd'),
  time: undefined,
  notes: undefined,
};

const AttachWorkflowToEventDialog = ({
  open,
  handleClose,
  description,
  assignedWorkflowPhaseId,
  userId,
}: AttachWorkflowDialogProps) => {
  const [existingEvents, setExistingEvents] = useState(null);

  const [scheduledEvents, isLoading, getScheduledEvents] = useApiCall(
    scheduledEventRequests.getScheduledEvents
  );

  const {
    control,
    formState: { errors },
    handleSubmit,
    register,
    reset,
    setValue,
    watch,
  } = useForm({
    defaultValues: defaultValues,
    resolver: zodResolver(IAttachToEventFormSchema),
  });

  const watchUseExisting = watch('useExisting');

  useEffect(() => {
    getScheduledEvents({ params: { userId: userId } });
  }, [userId]);

  useEffect(() => {
    const now = DateTime.now();
    const validEvents = scheduledEvents
      ?.filter(event => {
        return event.time ? +now < +DateTime.fromMillis(event.time) : false;
      })
      .sort((x, y) => x.time - y.time);
    if (validEvents?.length)
      reset({
        ...defaultValues,
        existingEventId: validEvents[0].id,
        existingEventNote: validEvents[0].note,
      });
    setExistingEvents(validEvents);
  }, [scheduledEvents]);

  const ANYTIME = -1;
  const API_DATE_FORMAT = 'yyyy-MM-dd';

  const timeOptions: SelectOption<number>[] = useMemo(
    () => [
      { label: 'Anytime', value: ANYTIME },
      ...scheduledEventDateTimeHelpers.getTimeOptions(),
    ],
    []
  );

  const dispatch = useAppDispatch();

  function handleCheck() {
    if (watchUseExisting) {
      reset();
    }
  }

  function handleOnClose() {
    reset();
    handleClose();
  }

  async function onSubmit(data: IAttachToEventForm) {
    if (!watchUseExisting) {
      const [formattedDate] = new Date(data.date).toISOString().split('T');
      const date = DateTime.fromFormat(formattedDate, API_DATE_FORMAT);
      const time = date.plus({ milliseconds: data.time }).toMillis();

      const scheduledEventRequest: CreateScheduledEventRequestDto = {
        date: formattedDate,
        time,
        type: data.eventType,
        ...(!!data.notes && { note: data.notes }),
      };

      const scheduledEvent = await dispatch(
        createScheduledEventForUser({
          body: scheduledEventRequest,
          params: {
            userId: userId,
          },
        })
      );

      await dispatch(
        createWorkflowScheduledEvent({
          body: {
            assignedWorkflowPhaseId: assignedWorkflowPhaseId,
            scheduledEventId: scheduledEvent['payload']['id'],
          },
        })
      );
    } else {
      await dispatch(
        createWorkflowScheduledEvent({
          body: {
            assignedWorkflowPhaseId: assignedWorkflowPhaseId,
            scheduledEventId: data.existingEventId,
          },
        })
      );
    }
    handleOnClose();
  }

  return (
    <Dialog
      maxWidth="md"
      open={open}
      onClose={handleOnClose}
      role="dialog"
      aria-label="Attach Workflow to Scheduled Event dialog"
    >
      <DialogContentFormContainer onSubmit={handleSubmit(onSubmit)}>
        <DialogHeader
          postfixAction={{
            title: 'close',
            icon: <CloseIcon />,
            callback: handleClose,
          }}
        >
          Attach Workflow to Event
        </DialogHeader>
        <DialogMainContent>
          <DescriptionContainer>{description}</DescriptionContainer>
          {isLoading && <Loader />}
          {!isLoading && (
            <>
              {!existingEvents?.length && (
                <p>No existing available scheduled events.</p>
              )}
              {!!existingEvents?.length && (
                <ToggleFormGroup>
                  <ControlledCheckbox
                    ariaLabel="Use existing event"
                    control={control}
                    defaultChecked={false}
                    label="Add to an existing event"
                    name="useExisting"
                    onCheck={handleCheck}
                  />
                </ToggleFormGroup>
              )}
              <EventFieldsContainer>
                {watchUseExisting && (
                  <EventContainer>
                    <Section>
                      <FormControl fullWidth>
                        <InputLabel id="select-label">
                          Existing scheduled events
                        </InputLabel>
                        <Controller
                          name="existingEventId"
                          control={control}
                          defaultValue={undefined}
                          render={({ field }) => (
                            <Select
                              aria-label="Existing event select"
                              labelId="select-label"
                              label="Existing scheduled events"
                              {...field}
                              onChange={e => {
                                setValue(
                                  'existingEventNote',
                                  existingEvents.find(
                                    x => x.id === e.target.value
                                  ).note
                                );
                                field.onChange(e);
                              }}
                            >
                              {existingEvents.map(event => {
                                return (
                                  <MenuItem
                                    key={`event-mi-${event.id}`}
                                    value={event.id}
                                  >
                                    {DateTime.fromMillis(
                                      event.time
                                    ).toLocaleString(DateTime.DATETIME_FULL)}
                                  </MenuItem>
                                );
                              })}
                            </Select>
                          )}
                        />
                      </FormControl>
                    </Section>
                    <Section>
                      <TextField
                        fullWidth
                        name="existingEventNote"
                        label="Notes"
                        aria-label="Existing event note"
                        {...register('existingEventNote')}
                        multiline
                        variant="outlined"
                        disabled
                      />
                    </Section>
                  </EventContainer>
                )}
                {!watchUseExisting && (
                  <EventContainer>
                    <p>Choose an event type</p>
                    <Section>
                      <ControlledRadioGroup
                        ariaLabel="Scheduled event type"
                        control={control}
                        name="eventType"
                        errors={errors}
                        row={false}
                        options={[
                          {
                            label: 'Phone call',
                            value: EventType.CALL,
                          },
                          {
                            label: 'In-person visit',
                            value: EventType.VISIT,
                          },
                        ]}
                      />
                    </Section>
                    <Section>
                      <ControlledDateField
                        control={control}
                        label="Appointment date"
                        name="date"
                        inputType="date"
                        disablePast
                      />
                    </Section>
                    <Section>
                      <ControlledSelect
                        control={control}
                        label={'Time'}
                        name={'time'}
                        options={timeOptions}
                        fullWidth
                      />
                    </Section>
                    <Section>
                      <ControlledTextField
                        control={control}
                        name="notes"
                        label="Notes"
                        aria-label="Scheduled event notes"
                        multiline
                        minRows={6}
                        maxRows={6}
                        required
                      />
                    </Section>
                  </EventContainer>
                )}
              </EventFieldsContainer>
            </>
          )}
        </DialogMainContent>
        {!isLoading && (
          <DialogFooter
            actions={[
              {
                text: 'Save',
                type: 'submit',
              },
            ]}
          />
        )}
      </DialogContentFormContainer>
    </Dialog>
  );
};

export default AttachWorkflowToEventDialog;
